import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Monthly calendar grid showing which slots are booked at a Theater Hall.
 * `availability` shape: [{date: "2026-05-15", slot: "evening"|"matinee"|"full_day", status: "confirmed"|"accepted", ...}]
 * `blackout` shape: ["2026-05-20", ...]
 *
 * Slot colouring:
 *   confirmed  → rose-500 filled
 *   accepted   → amber-400 filled (locked-in but not final-confirmed)
 *   blackout   → gray/hatched
 *   available  → transparent
 */
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const SLOT_ORDER = ["matinee", "evening"];
const SLOT_LABEL = { matinee: "M", evening: "E", full_day: "F" };

export default function HallAvailabilityCalendar({
  availability = [],
  blackout = [],
  window: win,
  onDayClick,
  accent = "#7C3AED",
}) {
  const [cursor, setCursor] = useState(() => {
    const today = new Date();
    return { y: today.getFullYear(), m: today.getMonth() };
  });

  // Build a lookup: date -> { [slot]: entry }
  const byDate = useMemo(() => {
    const m = {};
    for (const a of availability) {
      if (!a?.date) continue;
      const slot = a.slot === "full_day" ? "full_day" : (a.slot || "evening");
      if (!m[a.date]) m[a.date] = {};
      if (slot === "full_day") {
        m[a.date].matinee = a;
        m[a.date].evening = a;
      } else {
        m[a.date][slot] = a;
      }
    }
    return m;
  }, [availability]);

  const blackoutSet = useMemo(() => new Set(blackout || []), [blackout]);

  const [minY, minM, maxY, maxM] = useMemo(() => {
    const from = win?.from ? new Date(win.from) : new Date();
    const to   = win?.to   ? new Date(win.to)   : new Date();
    return [from.getFullYear(), from.getMonth(),
            to.getFullYear(),   to.getMonth()];
  }, [win?.from, win?.to]);

  const canPrev = cursor.y > minY || (cursor.y === minY && cursor.m > minM);
  const canNext = cursor.y < maxY || (cursor.y === maxY && cursor.m < maxM);
  const step = (d) => setCursor((c) => {
    const total = c.y * 12 + c.m + d;
    return { y: Math.floor(total / 12), m: total % 12 };
  });

  const monthDays = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const startWeekday = first.getDay(); // 0 Sun–6 Sat
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    // Lead in with empty pads to align first day to Monday-start grid.
    // We'll use Sun-start here for simplicity.
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(cursor.y, cursor.m, d);
      const iso = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ d, iso, dt });
    }
    // Pad tail to 6 rows × 7.
    while (cells.length < 42) cells.push(null);
    return cells;
  }, [cursor.y, cursor.m]);

  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div className="cs-card rounded-2xl p-4 sm:p-6" data-testid="hall-availability-calendar">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">Availability</div>
          <div className="font-display text-lg uppercase tracking-tight mt-1"
               data-testid="hall-avail-month-label">
            {MONTH_NAMES[cursor.m]} {cursor.y}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => canPrev && step(-1)}
            disabled={!canPrev}
            className="h-9 w-9 rounded-full bg-white/8 hover:bg-white/15 text-white flex items-center justify-center disabled:opacity-30 disabled:hover:bg-white/8 transition"
            data-testid="hall-avail-prev">
            <ChevronLeft size={16}/>
          </button>
          <button onClick={() => canNext && step(1)}
            disabled={!canNext}
            className="h-9 w-9 rounded-full bg-white/8 hover:bg-white/15 text-white flex items-center justify-center disabled:opacity-30 disabled:hover:bg-white/8 transition"
            data-testid="hall-avail-next">
            <ChevronRight size={16}/>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-3 flex flex-wrap items-center gap-3 text-[10px] tracking-[0.16em] uppercase text-white/55">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-rose-500"/>Confirmed</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-amber-400"/>Accepted</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-white/30"/>Blackout</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: accent }}/>Today</span>
        <span className="ml-auto text-[9px]">M · Matinee &nbsp; E · Evening</span>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 mb-1.5">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[9px] tracking-[0.18em] uppercase text-white/40 text-center pb-1">
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1">
        {monthDays.map((cell, i) => {
          if (!cell) return <div key={i} className="min-h-[54px]"/>;
          const slots = byDate[cell.iso] || {};
          const isBlackout = blackoutSet.has(cell.iso);
          const isToday    = cell.iso === todayIso;
          const isPast     = cell.iso < todayIso;
          const bookedAny  = Boolean(slots.matinee || slots.evening);
          const clickable  = onDayClick && !isBlackout && !isPast;
          return (
            <button
              key={cell.iso}
              onClick={() => clickable && onDayClick(cell.iso, slots)}
              disabled={!clickable}
              className={`min-h-[54px] rounded-md p-1.5 text-left border transition text-[10px] ${
                isPast ? "border-white/5 bg-white/[0.02] text-white/25" :
                isToday ? "border-2" :
                isBlackout ? "border-white/10 bg-white/5" :
                "border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/25"
              }`}
              style={isToday ? { borderColor: accent, background: `${accent}0d` } : {}}
              data-testid={`hall-avail-day-${cell.iso}`}>
              <div className={`font-semibold ${isToday ? "" : "text-white/85"}`}
                   style={isToday ? { color: accent } : {}}>
                {cell.d}
              </div>
              <div className="mt-1 flex gap-0.5">
                {SLOT_ORDER.map((s) => {
                  const entry = slots[s];
                  if (isBlackout) {
                    return <span key={s} className="flex-1 h-1.5 rounded-sm bg-white/25"/>;
                  }
                  if (!entry) {
                    return <span key={s} className="flex-1 h-1.5 rounded-sm bg-white/[0.06]"/>;
                  }
                  const tone = entry.status === "confirmed" ? "bg-rose-500" : "bg-amber-400";
                  return (
                    <span key={s}
                          className={`flex-1 h-1.5 rounded-sm ${tone}`}
                          title={`${SLOT_LABEL[s] || s}: ${entry.status}${entry.group_name ? " · " + entry.group_name : ""}`}
                    />
                  );
                })}
              </div>
              {bookedAny && (
                <div className="mt-0.5 text-[8px] tracking-widest text-white/45 truncate">
                  {(slots.evening?.group_name || slots.matinee?.group_name || "").split(" ")[0]}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
